// AllFeesTable — the fee catalogue: one row per charge the manager created,
// with a delete action gated on the canManageFees flag. Sprint 5d extraction.
//
// Sprint 135 (R12-2) — a condo-wide assessment is N per-owner rows on the server,
// shown here as ONE line. So `onRemove` hands back the whole summary, not a bare
// fee id: the caller needs the groupId to remove the batch rather than one
// owner's share (which used to leave N-1 owners charged while the line vanished).
import React from 'react';
import { Button, Space, Table, Tag, Tooltip } from 'antd';
import { ApartmentOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import type { FeeTypeSummary } from './feeRules';
import { feeTypeRowKey, formatCurrency } from './feeRules';

export interface AllFeesTableProps {
  rows: FeeTypeSummary[];
  canManageFees: boolean;
  onRemove: (row: FeeTypeSummary) => void;
}

export const AllFeesTable: React.FC<AllFeesTableProps> = ({ rows, canManageFees, onRemove }) => {
  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string, record: FeeTypeSummary) => (
        <Space>
          {record.isCondoWide ? (
            <Tooltip title="Condo-wide Fee (applies to all units)">
              <ApartmentOutlined className="text-blue-500" />
            </Tooltip>
          ) : (
            <Tooltip title="Individual Unit Fee">
              <UserOutlined className="text-green-500" />
            </Tooltip>
          )}
          <Tag color={record.isCondoWide ? 'blue' : 'green'}>
            {type
              .split(/[-_]/)
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
              .join(' ')}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: FeeTypeSummary) => (
        <Space direction="vertical" size="small">
          <span className="font-medium">{formatCurrency(amount)}</span>
          {record.isCondoWide && (
            <small className="text-gray-500">
              × {record.totalUnits} unit{record.totalUnits > 1 ? 's' : ''}
            </small>
          )}
          {/* Sprint 135 (R12-2) — a real settlement rollup for an assessment.
              Before, one owner's status was shown as the whole batch's, so a
              single payment could read as everyone having paid. */}
          {record.groupId && record.totalUnits > 1 && (
            <small className={record.paidUnits === record.totalUnits ? 'text-green-600' : 'text-gray-500'}>
              {record.paidUnits} of {record.totalUnits} paid
            </small>
          )}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: FeeTypeSummary) => (
        <Space>
          {canManageFees && (
            <Tooltip title={record.groupId && record.totalUnits > 1
              ? `Remove this assessment from all ${record.totalUnits} units`
              : 'Remove Fee'}>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onRemove(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={rows}
      // Sprint 141 (R13-2) — was rowKey="type". A condo-wide assessment is ONE
      // line per BATCH (Sprint 135), so two assessments of the same type are two
      // legitimate rows that collided on a type-only key. Shares the identity
      // builder with getUniqueFeeTypes so the two can never drift again.
      rowKey={feeTypeRowKey}
      pagination={{ pageSize: 10 }}
      scroll={{ x: 600 }}
    />
  );
};
